 // set the cut off time, no more next day after the cutoff
 const cutOffHour = '14:00'

 const isSaturday = (date) => date.day() === 6
 const isSunday = (date) => date.day() === 0
 const isWeekend = (date) => {
   const day = date.day()
   return day === 0 || day === 6
 }

 /**
 * This function returns the cut-off date and time for daily shipping based on the specified
 * parameters.
 * @param [dailyShippingDeadlineHour] - The daily shipping deadline hour is the hour of the day by
 * which orders must be placed in order to be shipped out on the same day. It is a parameter that can
 * be passed into the `getTodaysCutOffDateTime` function to customize the cutoff time. If no value is
 * provided,
 * @param [dailyShippingDeadlineTimeZone=Europe/London] - The `dailyShippingDeadlineTimeZone` parameter
 * is a string that represents the time zone in which the daily shipping deadline hour is defined. It
 * is used to convert the input date to the specified time zone, set the daily shipping deadline hour,
 * and then convert the resulting date back to the local time zone.
 * @param [startDate] - The startDate parameter is a Date object that represents the starting date and
 * time from which the cut-off date and time will be calculated. If no value is provided, it defaults
 * to the current date and time.
 * @returns The function `getTodaysCutOffDateTime` returns a `Date` object representing the cut-off
 * time for daily shipping, based on the provided parameters or default values. The cut-off time is
 * calculated based on the `dailyShippingDeadlineHour` and `dailyShippingDeadlineTimeZone` parameters,
 * and is returned in the local time zone of the user, based on the `startDate` parameter or the
 */
 const getTodaysCutOffDateTime = (
   dailyShippingDeadlineHour = cutOffHour,
   dailyShippingDeadlineTimeZone = 'Europe/London',
   startDate = new Date(),
 ) => {
   const localTimeZone = spacetime.now().timezone().name
   return new Date(
     spacetime(startDate, localTimeZone)
       .goto(dailyShippingDeadlineTimeZone)
       .time(dailyShippingDeadlineHour)
       .goto(localTimeZone)
       .iso(),
   )
 }

 
 /**
 * The function calculates the delivery date based on the cut-off hour, start date, and number of
 * working days, taking into account weekends and a specific time zone.
 * @param cutOffHour - The cut-off hour for shipping orders. Any orders placed after this hour will be
 * considered for the next working day's delivery.
 * @param [startDate] - The starting date for calculating the delivery date. If not provided, it
 * defaults to the current date.
 * @param [workingDays=1] - `workingDays` is the number of business days (excluding weekends and
 * holidays) it takes for a delivery to arrive. It is an optional parameter with a default value of 1,
 * meaning the delivery will arrive the next business day if no value is provided.
 * @returns The function `getDeliveryDate` returns a `Date` object representing the delivery date based
 * on the input parameters `cutOffHour`, `startDate`, and `workingDays`.
 */
 const getDeliveryDate = (
   cutOffHour,
   startDate = new Date(),
   workingDays = 1,
 ) => {
   const dailyShippingDeadlineTimeZone = 'Europe/London'
   const cutOffDateTime = getTodaysCutOffDateTime(cutOffHour, dailyShippingDeadlineTimeZone, startDate)
   const remoteStartDate = spacetime(startDate, dailyShippingDeadlineTimeZone)

   let daysToAdd = parseInt(workingDays, 10)
   let endDate = remoteStartDate

   if (
     (remoteStartDate.isAfter(cutOffDateTime) || remoteStartDate.isEqual(cutOffDateTime)) &&
     daysToAdd &&
     isWeekend(remoteStartDate) === false
   ) {
     daysToAdd++
   }

   for (let i = 0; i < daysToAdd; ++i) {
     if (isSaturday(endDate)) {
       endDate = endDate.add(2, 'day')
     } else if (isSunday(endDate)) {
       endDate = endDate.add(1, 'day')
     }
     endDate = endDate.add(1, 'day')
   }

   if (isSaturday(endDate)) {
     endDate = endDate.add(2, 'day')
   } else if (isSunday(endDate)) {
     endDate = endDate.add(1, 'day')
   }

   return new Date(endDate.iso())
 }

 /**
 * The function takes a date and returns a formatted string with the day of the week, date, and month.
 * @param date - The `date` parameter is a string or a Date object representing a specific date. It is
 * used as input to create a `spacetime` object, which is a library for working with dates and times in
 * JavaScript. The function `toLongDate` then formats the date into a long date
 * @returns The function `toLongDate` returns a string in the format of "DayName DateOrdinal
 * MonthName". For example, "Monday 1st January".
 */
 const toLongDate = (date) => {
   const s = spacetime(date)
   const dayName = s.dayName()
   const dayNameFirstUp = `${dayName.charAt(0).toUpperCase()}${dayName.slice(1)}`
   return `${dayNameFirstUp} ${s.date()}${getOrdinal(s.date())} ${s.format('month')}`
 }

 /**
 * The function returns the ordinal suffix for a given day.
 * @param day - The parameter `day` is a number representing a day of the month.
 * @returns The function `getOrdinal` returns a string representing the ordinal suffix for a given day.
 * If the day is between 4 and 20 (inclusive), or ends in 0, the function returns 'th'. Otherwise, it
 * returns 'st' for numbers ending in 1, 'nd' for numbers ending in 2, and 'rd' for numbers ending in
 * 3.
 */
 const getOrdinal = (day) => {
   if (day > 3 && day < 21) return 'th'
   switch (day % 10) {
     case 1:
       return 'st'
     case 2:
       return 'nd'
     case 3:
       return 'rd'
     default:
       return 'th'
   }
 }

 // Main func to call
 const calculateDelveryTimeDate = () => {
   const dailyShippingDeadlineTimeZone = 'Europe/London';
   const remoteDate = spacetime(new Date(), dailyShippingDeadlineTimeZone)
   const cutOffDateTime = spacetime(getTodaysCutOffDateTime(cutOffHour))
 
   const isTooLateForToday = remoteDate.isAfter(cutOffDateTime) || remoteDate.isEqual(cutOffDateTime)
   const nextCutOffDateTime = isTooLateForToday ? cutOffDateTime.add(1, 'day') : cutOffDateTime
   const currentSecondsRemaining = remoteDate.diff(nextCutOffDateTime, 'second')
   const minutes = Math.floor((currentSecondsRemaining % 3600) / 60)
   const hours = Math.floor(currentSecondsRemaining / 3600)
 
   document.querySelector('.date').textContent = toLongDate(getDeliveryDate(cutOffHour))
   document.querySelector('.hours').textContent = hours
   document.querySelector('.minutes').textContent = minutes
 }

// Call on load and then interval will refresh the time
calculateDelveryTimeDate()

// Update every 30 seconds
setInterval(calculateDelveryTimeDate, 30000)